import React, { useCallback, useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, ClipboardList } from "lucide-react";
import { PracticeEditor } from "@/components/practice/PracticeEditor";

function PracticeList({ practices, activeId, setActiveId, loading }) {
  return (
        <aside className="space-y-2">
          <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-slate-500 mb-2">Recent Practices</div>
          {loading && <div className="text-sm text-slate-400">Loading…</div>}
          {!loading && practices.length === 0 && (
            <div className="text-sm text-slate-500 italic">None yet — click New Practice.</div>
          )}
          {practices.map(p => (
            <button
              key={p.id || 'draft'}
              onClick={() => setActiveId(p.id)}
              data-testid={`practice-item-${p.id || 'draft'}`}
              className={`w-full text-left px-4 py-3 border transition-colors ${
                (p.id && p.id === activeId) || (!p.id && !activeId)
                  ? "border-amber bg-surface"
                  : "border-surface-3 hover:border-amber/50 bg-surface"
              }`}
            >
              <div className="flex items-center gap-2">
                <ClipboardList className="w-3 h-3 text-amber" />
                <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">{p.date}</div>
              </div>
              <div className="font-semibold text-sm mt-1">{p.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{(p.periods || []).length} periods</div>
            </button>
          ))}
        </aside>
  );
}

export default function PracticePlanner() {
  const [practices, setPractices] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/practices");
      setPractices(data);
      if (data.length) setActiveId(prev => prev || data[0].id);
    } catch (e) {
      console.error("Failed to load practices", e);
      toast.error("Failed to load practices. Please try again.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  const active = practices.find(p => p.id === activeId);

  const newPractice = () => {
    const draft = {
      id: null, title: "New Practice",
      date: new Date().toISOString().slice(0,10),
      start_time: "15:30", location: "", weather: "",
      objectives: "", attendance_expected: 68, periods: []
    };
    setPractices([draft, ...practices.filter(p => p.id)]);
    setActiveId(null);
  };

  const onSaved = (saved) => {
    setPractices(prev => {
      const others = prev.filter(p => p.id && p.id !== saved.id);
      return [saved, ...others];
    });
    setActiveId(saved.id);
  };

  const onDeleted = (id) => {
    const next = practices.filter(p => p.id !== id);
    setPractices(next);
    setActiveId(next[0]?.id || null);
  };

  const drafting = practices.find(p => !p.id);

  return (
    <div data-testid="practice-planner-page">
      <TopBar
        title="Practice Planner"
        subtitle="Drag to reorder · drop plays from library"
        actions={
          <Button onClick={newPractice} data-testid="new-practice-btn"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-[0.25em]">
            <Plus className="w-4 h-4 mr-1"/> New Practice
          </Button>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 p-8">
        <PracticeList practices={practices} activeId={activeId} setActiveId={setActiveId} loading={loading} />
        <div>
          {drafting && <PracticeEditor practice={drafting} onSaved={onSaved} onDeleted={onDeleted} />}
          {!drafting && active && <PracticeEditor practice={active} onSaved={onSaved} onDeleted={onDeleted} />}
          {!active && practices.length === 0 && !loading && (
            <div className="text-sm text-slate-500 italic p-12 border border-dashed border-surface-3 text-center">
              Click "New Practice" to build your first card.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
